import { useListIntegrationsAuthenticated } from "@/lib/api/generated";
import { logoUrl } from "@/features/settings/lib/integrations/catalog";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { useAtsConnections } from "@/features/ats-sync/hooks/use-ats-connections";

/**
 * Logo URL for an ATS connector id (e.g. "ashby"), resolved from the backend
 * integrations catalog. Returns undefined until the catalog loads or when the
 * connector has no catalog entry, so callers can fall back to a generic icon.
 */
export function useConnectorLogo(
  connectorId?: string | null,
): string | undefined {
  const { data } = useListIntegrationsAuthenticated();
  if (!connectorId) return undefined;
  const item = data?.integrations.find((i) => i.id === connectorId);
  const url = item ? logoUrl(item.domain) : "";
  return url || undefined;
}

/**
 * Logo for the job's active ATS connection — resolves connector_domain from the
 * connections list first (always available once connections load), then falls
 * back to the integrations catalog lookup by connector id.
 */
export function useJobConnectorLogo(jobId: string | null): string | undefined {
  const { connectionId } = useJobAtsLink(jobId);
  const { connections } = useAtsConnections();
  const conn = connections.find((c) => c.id === connectionId);
  const domainLogo = conn?.connector_domain
    ? logoUrl(conn.connector_domain) || undefined
    : undefined;
  const catalogLogo = useConnectorLogo(conn?.connector_id);
  return domainLogo ?? catalogLogo;
}
